<script setup>
import { computed, ref } from 'vue'
import { useQuasar } from 'quasar'
import DeviceDialog from './DeviceDialog.vue'

const props = defineProps({
  address: {
    type: Object,
    required: true,
  },
  width: {
    type: Number,
    default: 180,
  },
})

const $q = useQuasar()

const PADDING = 14
const permitDialog = ref(false)
const selectedDevice = ref(null)

const addressDevices = computed(() =>
  Array.isArray(props.address?.devices) ? props.address.devices : []
)

const allCoords = computed(() => {
  const pts = []
  if (Array.isArray(props.address?.locationPolygon)) {
    props.address.locationPolygon.forEach(([lng, lat]) => {
      if (typeof lng === 'number' && typeof lat === 'number') pts.push({ lng, lat })
    })
  }
  addressDevices.value.forEach((d) => {
    if (Array.isArray(d.location) && d.location.length >= 2) {
      const [lng, lat] = d.location
      if (typeof lng === 'number' && typeof lat === 'number') pts.push({ lng, lat })
    }
  })
  return pts
})

const bounds = computed(() => {
  const coords = allCoords.value
  if (!coords.length) return null
  const minLng = Math.min(...coords.map((c) => c.lng))
  const maxLng = Math.max(...coords.map((c) => c.lng))
  const minLat = Math.min(...coords.map((c) => c.lat))
  const maxLat = Math.max(...coords.map((c) => c.lat))
  return { minLng, maxLng, minLat, maxLat }
})

const computedHeight = computed(() => {
  const b = bounds.value
  if (!b) return 120

  const rangeX = b.maxLng - b.minLng || 0.0001
  const rangeY = b.maxLat - b.minLat || 0.0001

  const avgLat = (b.minLat + b.maxLat) / 2
  const correctedRangeX = rangeX * Math.cos((avgLat * Math.PI) / 180) || 0.0001

  const naturalHeight = props.width * (rangeY / correctedRangeX)
  return Math.min(400, Math.max(80, Math.round(naturalHeight)))
})

function toSvg(lng, lat) {
  const b = bounds.value
  if (!b) return { x: 0, y: 0 }

  const drawW = props.width - PADDING * 2
  const drawH = computedHeight.value - PADDING * 2

  const rangeX = b.maxLng - b.minLng || 0.0001
  const rangeY = b.maxLat - b.minLat || 0.0001

  const scaleX = drawW / rangeX
  const scaleY = drawH / rangeY
  const scale = Math.min(scaleX, scaleY)

  const offsetX = PADDING + (drawW - rangeX * scale) / 2
  const offsetY = PADDING + (drawH - rangeY * scale) / 2

  return {
    x: offsetX + (lng - b.minLng) * scale,
    y: computedHeight.value - (offsetY + (lat - b.minLat) * scale),
  }
}

const polygonPoints = computed(() => {
  if (!Array.isArray(props.address?.locationPolygon)) return ''
  return props.address.locationPolygon
    .filter((pt) => Array.isArray(pt) && pt.length >= 2)
    .map(([lng, lat]) => {
      const { x, y } = toSvg(lng, lat)
      return `${x},${y}`
    })
    .join(' ')
})

const deviceSvgPositions = computed(() =>
  addressDevices.value
    .filter((d) => Array.isArray(d.location) && d.location.length >= 2)
    .map((d) => {
      const { x, y } = toSvg(d.location[0], d.location[1])
      return { ...d, _x: x, _y: y }
    })
)

function onDeviceClick(device) {
  selectedDevice.value = device
  permitDialog.value = true
}
</script>

<template>
  <svg
    :width="width"
    :height="computedHeight"
    :viewBox="`0 0 ${width} ${computedHeight}`"
    style="display:block; overflow:visible;"
  >
    <rect
      x="0" y="0"
      :width="width" :height="computedHeight"
      rx="6"
      :fill="$q.dark.isActive ? '#1a1a2e' : '#f5f5ff'"
    />

    <polygon
      v-if="polygonPoints"
      :points="polygonPoints"
      fill="rgba(154, 7, 191, 0.18)"
      stroke="#9a07bf"
      stroke-width="1.5"
      stroke-linejoin="round"
    />

    <g
      v-for="device in deviceSvgPositions"
      :key="device._id"
      style="cursor:pointer;"
      @click.stop="onDeviceClick(device)"
    >
      <template v-if="device.isOnline">
        <circle :cx="device._x" :cy="device._y" r="5" fill="none" stroke="rgba(0,200,83,0.7)" stroke-width="1.5">
          <animate attributeName="r" from="5" to="22" dur="2.2s" begin="0s" repeatCount="indefinite" />
          <animate attributeName="stroke-opacity" from="0.8" to="0" dur="2.2s" begin="0s" repeatCount="indefinite" />
        </circle>
        <circle :cx="device._x" :cy="device._y" r="5" fill="none" stroke="rgba(0,200,83,0.6)" stroke-width="1.5">
          <animate attributeName="r" from="5" to="22" dur="2.2s" begin="1.1s" repeatCount="indefinite" />
          <animate attributeName="stroke-opacity" from="0.8" to="0" dur="2.2s" begin="1.1s" repeatCount="indefinite" />
        </circle>
      </template>
      <circle
        :cx="device._x" :cy="device._y" r="5"
        :fill="device.isOnline ? '#00c853' : '#9e9e9e'"
        stroke="white" stroke-width="2"
      >
        <title>{{ device.deviceNumber || device.deviceId }}</title>
      </circle>
    </g>

    <text
      v-if="!polygonPoints"
      :x="width / 2" :y="computedHeight / 2"
      text-anchor="middle"
      dominant-baseline="middle"
      font-size="11"
      :fill="$q.dark.isActive ? '#666' : '#aaa'"
    >No polygon</text>
  </svg>

  <DeviceDialog
    v-model="permitDialog"
    :device="selectedDevice"
    :address="address"
  />
</template>
